import { queryKeys, useHttpClient, useActiveOrganizationId } from '@repo/api-client/react';
import type { Client } from '@repo/api-client/dtos';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { deleteJSON } from '../internal/http';

const OkSchema = z.object({ ok: z.boolean() }).passthrough();

export function useDeleteClient() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const orgId = useActiveOrganizationId();

  const mutation = useMutation({
    mutationFn: async (clientId: string) => {
      if (!orgId) throw new Error('No active organization');
      await deleteJSON(http, `/orgs/${orgId}/clients/${clientId}`, OkSchema);
      return clientId;
    },
    onSuccess: (clientId) => {
      if (!orgId) return;
      queryClient.removeQueries({ queryKey: queryKeys.clients.detail(orgId, clientId) });
      // List cache is `InfiniteData<{ items, nextCursor? }>` since
      // the B1 (2026-05-09) migration to `useInfiniteQuery`.
      type ClientsPage = { items: Client[]; nextCursor?: string };
      type ClientsCache = { pages: ClientsPage[]; pageParams: unknown[] };
      queryClient.setQueryData<ClientsCache>(
        queryKeys.clients.all(orgId),
        (cache) =>
          cache
            ? {
                ...cache,
                pages: cache.pages.map((page) => ({
                  ...page,
                  items: page.items.filter((c) => c.id !== clientId),
                })),
              }
            : cache,
      );
    },
  });

  return {
    deleteClient: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
